/**
 * 수직 레이아웃 사진 카드 (V2용)
 * PDF 보고서 스타일: 사진을 위에 크게 배치하고 아래에 발견사항 나열
 * 위험영역 편집 가능 (이동, 크기 조절, 삭제)
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Badge } from '@safetyhub/ui';
import { HazardOverlay } from './HazardOverlay';
import { InteractiveHazardEditor } from './InteractiveHazardEditor';
import { AnnotationOverlay } from './AnnotationOverlay';
import { FindingDetail, FindingEditHandlers } from './FindingDetail';
import type { HazardEditState } from './FindingsPanel';
import { DotIcon } from '../../icons';
import type { AIAnalysisDetail, FindingModifications, BoundingBox, SelectedMeasureEntry } from '../../../lib/inspections';
import { normalizeFileUrl } from '../../../lib/api';
import { PhotoMetaSection } from './PhotoMetaSection';
import { getEvidenceDisplayName, measureRenderedImageBox, type PhotoPurposeInUse } from '@safetyhub/shared';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'GOOD';
type AnalysisStatus = 'PENDING' | 'ANALYZING' | 'COMPLETED' | 'FAILED';

interface Finding {
  id: string;
  category: string;
  subCategory?: string;
  description: string;
  severity: Severity;
  aiGenerated: boolean;
  aiConfidence?: number;
  aiAnalysisDetail?: AIAnalysisDetail;
  modifications?: FindingModifications;
  deletedAt?: string;
  deletedBy?: string;
  aiLikelihoodScore?: number | null;
  aiSeverityScore?: number | null;
  // TBM 분류 축 (규약 ③)
  analyzedWorkCategory?: string | null;
  confirmedWorkCategory?: string | null;
  // 해소 관련
  resolutionStatus?: 'PENDING' | 'COMPLIED' | 'NON_COMPLIED' | 'PARTIAL';
  resolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
  resolutionPhotoUrl?: string;
  notes?: string;
  resolvedByUser?: { id: string; name: string };
  resolvedAt?: string;
  selectedMeasures?: SelectedMeasureEntry[];
}

interface Photo {
  id: string;
  url: string;
  thumbnailUrl?: string;
  status: 'UPLOADING' | 'COMPLETED' | 'FAILED';
  analysisStatus: AnalysisStatus;
  // ⚠️ `analysisSummary`·`analysisError`는 2026-10-05에 걷었다 — 아래 「분석에 실패했습니다.」
  //    자리의 주석이 그 이유의 소유자다. 되살리기 전에 그것을 먼저 읽을 것.
  findings: Finding[];
  isReviewed: boolean;
  addedById?: string;
  addedBy?: {
    id: string;
    name: string;
  };
  /** 사진 추가 시점 스냅샷 이름 (서버 신설 필드) */
  addedBySnapshotName?: string | null;
  /**
   * ⚠️ DB enum보다 좁다 — 「오늘 쓰는 용도」다. 이 모듈이 그 값을 **이진으로** 읽는 자리가
   * 가장 많으므로(10자리) 되살릴 때 먼저 볼 곳이다 —
   * `@safetyhub/shared`의 `PHOTO_PURPOSES_DORMANT` JSDoc의 「되살릴 때 해야 할 일」 셋째.
   */
  purpose?: PhotoPurposeInUse;
  location?: { id: string; name: string } | null;
  detailLocation?: { id: string; name: string } | null;
  equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null;
  processType?: string | null;
}

interface PhotoCardVerticalProps {
  photo: Photo;
  findings: Finding[];
  /** 사진 번호 (1부터 시작) */
  index: number;
  /** 발견사항 번호 시작 (이전 사진들의 발견사항 수 합계 + 1) */
  findingStartIndex: number;
  /** 검토 완료 여부 */
  isReviewed: boolean;
  /** 위험영역 표시 여부 */
  showHazardOverlay?: boolean;
  /** 위험영역 표시 토글 핸들러 */
  onShowHazardOverlayChange?: (show: boolean) => void;
  /** 검토 토글 핸들러 (undefined면 토글 불가) */
  onReviewToggle?: (checked: boolean) => void;
  /** 검토 비활성화 여부 */
  reviewDisabled?: boolean;
  /** 사진 클릭 핸들러 */
  onPhotoClick?: () => void;
  /** 메뉴 클릭 핸들러 @deprecated headerAction 사용 */
  onMenuClick?: () => void;
  /** 헤더 우측 액션 슬롯 (Desktop: DropdownMenu, Mobile: ActionSheet 트리거) */
  headerAction?: React.ReactNode;
  /** 중점 분석 카테고리 (빈 배열이 아닌 경우 메시지 분기) */
  focusCategories?: string[];
  /** 선택 가능 여부 */
  selectable?: boolean;
  /** 선택 상태 */
  isSelected?: boolean;
  /** 선택 변경 핸들러 */
  onSelectionChange?: (checked: boolean) => void;
  /** 등록자 표시 여부 */
  showAddedBy?: boolean;
  /** 강조된 발견사항 ID */
  highlightedFindingId?: string;
  /** 발견사항 클릭 핸들러 */
  onFindingClick?: (findingId: string) => void;
  /** 편집 가능 여부 */
  editable?: boolean;
  /** 발견사항 편집 핸들러 */
  editHandlers?: FindingEditHandlers;
  /** 위험 영역 편집 상태 */
  hazardEdits?: HazardEditState[];
  /** 위험영역 변경 핸들러 (드래그 중 실시간) */
  onHazardChange?: (findingId: string, boundingBox: BoundingBox | null) => void;
  /** 위험영역 변경 완료 핸들러 (드래그 종료 시 서버 저장용) */
  onHazardChangeEnd?: (findingId: string, boundingBox: BoundingBox) => void;
  /** 위험영역 삭제 저장 핸들러 (삭제 버튼 클릭 시 서버 저장용) */
  onHazardDeleteSave?: (findingId: string) => void;
  /** 위험영역 되돌리기 핸들러 */
  onHazardRestore?: (findingId: string) => void;
  /** 그리기 메모 오버레이 표시 여부 */
  showAnnotationOverlay?: boolean;
  /** 조직 ID (어노테이션 + 장소/위치 선택용) */
  companyId?: string;
  /** 현장 ID (장소/위치 선택용) */
  siteId?: string;
  /** 점검 ID (어노테이션 로드용) */
  inspectionId?: string;
  /** 위험요인 해소 핸들러 */
  onResolveHazard?: (findingId: string) => void;
  /** 보고서 생성됨 여부 */
  hasReport?: boolean;
  /** 점검완료 잠금 여부 */
  isCompletionLocked?: boolean;
  /** 위험요인 해소 피처 플래그 활성화 여부 */
  hazardResolutionEnabled?: boolean;
  /** 발견사항 추가 핸들러 */
  onAddFinding?: () => void;
  /** 발견사항 보고서 포함 선택 기능 활성화 여부 */
  findingSelectionEnabled?: boolean;
  /** 개선대책 보고서 포함 여부 토글 */
  onToggleMeasureSelection?: (findingId: string, measure: string, excluded: boolean) => void;
  /** 현재 로그인 사용자 ID (본인 선택 해제 권한 판별용) */
  currentUserId?: string;
  /** 현재 사용자가 completion 서명을 마쳤는지 (완료 후 해제 불가) */
  isMyCompleted?: boolean;
  /** 현재 사용자가 점검 참여자 중 PRIMARY 역할인지 (override 권한) */
  isPrimary?: boolean;
  /** 현재 사용자가 조직 Owner인지 (override 권한) */
  isOwner?: boolean;
  /** 수평 레이아웃 (PC: 좌측 사진, 우측 발견사항) */
  horizontalLayout?: boolean;
  /** 수평 레이아웃 사진 sticky top 오프셋 (StickyHeader 높이 등) */
  stickyTopOffset?: number;
  /** 사진 메타데이터 업데이트 콜백 */
  onPhotoMetaUpdate?: (photoId: string, updates: { location?: { id: string; name: string } | null; detailLocation?: { id: string; name: string } | null; equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null; processType?: string | null }) => void;
  /** Phase N3: 위험성평가 설정 (score 기반 SeverityBadge 렌더링용) */
  riskConfig?: import('@safetyhub/shared').RiskAssessmentConfig | null;
  /**
   * 카드 껍데기 결. 기본 `default`는 폴드·폰이 쓰는 지금 모습이고,
   * `aligned`는 시안 `.insp-card`(radius 18 · line 테두리 · 헤더 그라데이션 · 번호 배지)다.
   * 데스크톱만 넘긴다 — 기본값을 시안으로 올리면 아직 정렬 전인 폴드·폰까지 끌려간다.
   */
  appearance?: 'default' | 'aligned';
}

export function PhotoCardVertical({
  photo,
  findings,
  index,
  findingStartIndex,
  isReviewed,
  showHazardOverlay = false,
  onShowHazardOverlayChange,
  onReviewToggle,
  reviewDisabled = false,
  onPhotoClick: _onPhotoClick,
  onMenuClick: _onMenuClick,
  headerAction,
  selectable = false,
  isSelected = false,
  onSelectionChange,
  showAddedBy = false,
  highlightedFindingId,
  onFindingClick,
  editable = false,
  editHandlers,
  hazardEdits = [],
  onHazardChange,
  onHazardChangeEnd,
  onHazardDeleteSave,
  onHazardRestore,
  showAnnotationOverlay = false,
  companyId,
  siteId,
  inspectionId,
  onResolveHazard,
  hasReport = false,
  isCompletionLocked = false,
  hazardResolutionEnabled = false,
  onAddFinding,
  findingSelectionEnabled = false,
  onToggleMeasureSelection,
  focusCategories = [],
  horizontalLayout = false,
  stickyTopOffset = 0,
  onPhotoMetaUpdate,
  currentUserId,
  isMyCompleted = false,
  isPrimary = false,
  isOwner = false,
  riskConfig,
  appearance = 'default',
}: PhotoCardVerticalProps) {
  // 시안 정렬 결. 내부 구분선·헤더 바탕·번호 표기가 이 한 축으로 갈린다.
  const isAligned = appearance === 'aligned';
  const dividerClass = isAligned ? 'border-line-soft' : 'border-ink-300';
  const headerRowClass = isAligned
    ? 'bg-gradient-to-b from-white to-surface-soft'
    : 'bg-ink-100';
  const selectCheckboxClass = isAligned
    ? 'h-[18px] w-[18px] cursor-pointer rounded-[5px] accent-brand-600'
    : 'w-3.5 h-3.5 rounded border-ink-300 text-brand-600 focus:ring-brand-500';
  // 사진 번호 — 시안 `.num-badge`(brand 그라데이션 사각 배지). 기본은 지금처럼 텍스트.
  const photoNumber = isAligned ? (
    <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-card-sm bg-gradient-to-br from-brand-600 to-brand-500 text-[13px] font-extrabold tabular-nums tracking-[-0.01em] text-white">
      #{index}
    </span>
  ) : (
    <>#{index}</>
  );

  const [imageError, setImageError] = useState(false);
  const photoContainerRef = useRef<HTMLDivElement>(null);
  const [imageDisplayInfo, setImageDisplayInfo] = useState({
    width: 0,
    height: 0,
    offsetX: 0,
    offsetY: 0,
  });

  // 선택된 발견사항 ID (위험 영역 강조용)
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);

  // 삭제되지 않은 발견사항만 필터링 (표시용)
  const activeFindings = findings.filter(f => !f.deletedAt);

  // 사용자 추가/삭제 배지 카운트
  const addedByUserCount = useMemo(
    () => findings.filter(f => f.aiGenerated === false && !f.deletedAt).length,
    [findings]
  );
  const deletedCount = useMemo(
    () => findings.filter(f => !!f.deletedAt).length,
    [findings]
  );

  // 보고서 생성 후 사진의 모든 발견사항이 미포함된 경우
  const isBpPhoto = photo.purpose === 'BEST_PRACTICE';
  const isBpFindingIncluded = (f: Finding) => {
    if (f.severity !== 'GOOD') return false;
    // null/undefined → included by default, [] → excluded, ['__bp_included__'] → included
    return !Array.isArray(f.selectedMeasures) || f.selectedMeasures.length > 0;
  };
  const allFindingsExcluded = hasReport && findingSelectionEnabled &&
    activeFindings.length > 0 &&
    activeFindings.every(f => isBpPhoto
      ? !isBpFindingIncluded(f)
      : !f.selectedMeasures || f.selectedMeasures.length === 0
    );
  // 일부만 포함된 경우 (N/M건 포함 표시용)
  const includedFindingCount = hasReport && findingSelectionEnabled
    ? activeFindings.filter(f => isBpPhoto
        ? isBpFindingIncluded(f)
        : f.selectedMeasures && f.selectedMeasures.length > 0
      ).length
    : 0;

  // 위험 영역 편집 상태 맵 생성
  const hazardEditMap = new Map(hazardEdits.map(e => [e.findingId, e]));

  /**
   * 이미지 크기 측정 (HazardOverlay용) — showHazardOverlay 없이도 측정.
   *
   * **object-contain 내용 상자 수식은 이 파일이 아니라 shared `measureRenderedImageBox`가
   * 소유한다**(`packages/shared/src/utils/analysis-region-render.ts`, 값 spec 보유). 종전에는
   * `AnalysisRegionEditor`·`PhotoViewerWithEditor`와 같은 수식이 세 벌 있었다(2026-10-08 P4-l).
   *
   * ⚠️ **이 자리의 거동 spec은 원리적으로 불가능하다** — 아래 오버레이들은
   * `imageDisplayInfo.width > 0`일 때만 렌더되고 그 값은 측정된 요소 크기·`naturalWidth`에서
   * 나오는데 **jsdom은 레이아웃을 계산하지 않아 늘 0**이다. 즉 이 변환이 틀려도 깨지는 테스트가
   * 없다. 기계로 잴 수 있는 것은 **값 층**(그 함수의 짝 spec)뿐이고, 「박스가 사진 그 자리에
   * 얹히는가」의 판정은 **브라우저 실측**이다(선례:
   * `docs/verification/2026-10-08-bbox-browser-measure/`). 여기를 바꾸면 거기서 다시 재라 —
   * 초록을 근거로 쓰지 말 것.
   *
   * ⚠️ 요소 크기는 **`getBoundingClientRect()`로 잰다 — `clientWidth`로 되돌리지 말 것.** 그 값은
   * **정수로 반올림**되어(예 223.86 → 224) 상자와 이미지의 비율이 «같은» 자리에서도 두 배율을
   * 어긋나게 만들고, 그러면 엉뚱한 분기가 뽑혀 여백이 밀린다. 이것은 「더 정확해서」가 아니라
   * **계약이다** — `AnalysisRegionRectInput.elementWidth`의 JSDoc이 호출부에 rect를 요구한다
   * (2026-10-08 P4-l에서 올렸다. 그전에는 이 자리만 `clientWidth`였다). 이 `<img>`는 border·padding이
   * 없어(전역 CSS에 `img` 규칙 0건 + preflight `border-width: 0`) 두 값의 차이는 서브픽셀뿐이고,
   * 조상에 CSS transform도 없어 rect가 스케일된 좌표를 주지도 않는다 — 둘 다 그때 확인했다.
   */
  useEffect(() => {
    if (!photoContainerRef.current) return;

    const updateSize = () => {
      if (photoContainerRef.current) {
        const img = photoContainerRef.current.querySelector('img') as HTMLImageElement;
        if (!img) return;

        // 게이트와 측정이 **같은 값**을 보게 rect를 한 번만 재서 둘에 쓴다 — 한쪽만 rect로
        // 올리면 「게이트는 통과하는데 측정은 다른 상자를 본다」가 된다.
        const rect = img.getBoundingClientRect();
        if (img.naturalWidth && img.naturalHeight && rect.width > 0 && rect.height > 0) {
          const box = measureRenderedImageBox({
            elementWidth: rect.width,
            elementHeight: rect.height,
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
            objectFit: 'contain',
          });
          // 위 가드가 네 값을 모두 양수로 좁히므로 `null`은 오지 않는다 — 타입 좁히기용.
          if (!box) return;

          setImageDisplayInfo({
            width: box.width,
            height: box.height,
            offsetX: box.left,
            offsetY: box.top,
          });
        }
      }
    };

    const img = photoContainerRef.current.querySelector('img');
    const onLoad = () => updateSize();

    if (img) {
      img.addEventListener('load', onLoad);
      // 캐시된 이미지: load 이벤트가 이미 발생했으므로 레이아웃 완료 후 직접 측정
      if (img.complete && img.naturalWidth > 0) {
        requestAnimationFrame(updateSize);
      }
    }

    const observer = new ResizeObserver(updateSize);
    if (img) {
      observer.observe(img);
    }

    const onResize = () => updateSize();
    window.addEventListener('resize', onResize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      if (img) {
        img.removeEventListener('load', onLoad);
      }
    };
  // horizontalLayout 변경 시 새 img에 observer 재연결 필요
  }, [horizontalLayout]);

  // 위험 영역 데이터 생성 (편집 상태 반영)
  const hazardAreas = showHazardOverlay
    ? activeFindings
        .filter(f => {
          // 편집 상태에서 삭제된 것은 제외
          const edit = hazardEditMap.get(f.id);
          if (edit?.isDeleted) return false;
          // 편집된 boundingBox가 있거나 원본 boundingBox가 있는 경우
          return edit?.current || f.aiAnalysisDetail?.boundingBox;
        })
        .map((f) => {
          const edit = hazardEditMap.get(f.id);
          const deletedCountBefore = findings
            .slice(0, findings.indexOf(f))
            .filter(x => x.deletedAt).length;
          return {
            id: f.id,
            number: findingStartIndex + findings.indexOf(f) - deletedCountBefore,
            // 편집된 boundingBox 우선, 없으면 원본
            boundingBox: edit?.current || f.aiAnalysisDetail!.boundingBox!,
            aiLikelihoodScore: f.aiLikelihoodScore ?? f.aiAnalysisDetail?.riskAssessment?.likelihood ?? null,
            aiSeverityScore: f.aiSeverityScore ?? f.aiAnalysisDetail?.riskAssessment?.severity ?? null,
            aiRiskLevel: f.aiAnalysisDetail?.riskAssessment?.riskLevel ?? null,
          };
        })
    : [];

  // 분석 상태 표시
  const getAnalysisStatusBadge = () => {
    switch (photo.analysisStatus) {
      case 'PENDING':
        return (
          <Badge tone="slate" shape="rounded">
            분석 대기
          </Badge>
        );
      case 'ANALYZING':
        return (
          <Badge tone="brand" shape="rounded">
            <DotIcon className="h-2 w-2 animate-pulse" />
            분석 중
          </Badge>
        );
      case 'FAILED':
        return (
          <Badge tone="rose" shape="rounded">
            분석 실패
          </Badge>
        );
      default:
        return null;
    }
  };

  // 실제 선택된 발견사항 ID (외부에서 전달된 것 또는 내부 상태)
  const effectiveSelectedFindingId = highlightedFindingId || selectedFindingId;

  // 위험영역 선택 핸들러 (위험영역 클릭 → 발견사항 선택)
  const handleHazardSelect = useCallback((hazardId: string | null) => {
    setSelectedFindingId(hazardId);
    if (hazardId) {
      onFindingClick?.(hazardId);
    }
  }, [onFindingClick]);

  // 위험영역 변경 핸들러
  const handleHazardChangeInternal = useCallback((hazardId: string, boundingBox: BoundingBox) => {
    onHazardChange?.(hazardId, boundingBox);
  }, [onHazardChange]);

  // 위험영역 삭제 핸들러
  const handleHazardDelete = useCallback((hazardId: string) => {
    // 로컬 상태 업데이트
    onHazardChange?.(hazardId, null);
    // 서버에 삭제 저장
    onHazardDeleteSave?.(hazardId);
  }, [onHazardChange, onHazardDeleteSave]);

  // 위험영역 변경 완료 핸들러 (서버 저장용)
  const handleHazardChangeEnd = useCallback((hazardId: string, boundingBox: BoundingBox) => {
    onHazardChangeEnd?.(hazardId, boundingBox);
  }, [onHazardChangeEnd]);

  // 발견사항 클릭 핸들러 (발견사항 클릭 → 위험영역 선택)
  const handleFindingSelect = useCallback((findingId: string, hasBoundingBox: boolean) => {
    // 위험영역 표시 자동 활성화
    if (hasBoundingBox && !showHazardOverlay && onShowHazardOverlayChange) {
      onShowHazardOverlayChange(true);
    }
    // 토글 동작
    setSelectedFindingId(effectiveSelectedFindingId === findingId ? null : findingId);
    onFindingClick?.(findingId);
  }, [showHazardOverlay, onShowHazardOverlayChange, effectiveSelectedFindingId, onFindingClick]);

  // 편집 모드 여부 (위험영역 편집 가능 조건)
  const canEditHazard = editable && onHazardChange;

  // 발견사항 렌더링 (수직/수평 공통)
  const renderFindings = () => {
    if (findings.length > 0) {
      return findings.map((finding, findingIdx) => {
        const deletedCountBefore = findings
          .slice(0, findingIdx)
          .filter(f => f.deletedAt).length;
        const globalNumber = finding.deletedAt
          ? undefined
          : findingStartIndex + findingIdx - deletedCountBefore;

        const edit = hazardEditMap.get(finding.id);
        const hasBoundingBox = !finding.deletedAt && !edit?.isDeleted && (
          edit?.current || finding.aiAnalysisDetail?.boundingBox
        );
        const isSelectedFinding = effectiveSelectedFindingId === finding.id;

        const hasLocalHazardEdit = edit !== undefined && (edit.current !== undefined || edit.isDeleted === true);
        const hasServerHazardEdit = finding.modifications?.modifiedFields?.some(f => f.field === 'boundingBox') ?? false;
        const hasHazardEdit = hasLocalHazardEdit || hasServerHazardEdit;

        const isBpPhoto = photo.purpose === 'BEST_PRACTICE';

        // 잠금 상태에서 선택된 개선대책이 없으면 발견사항 전체를 흐리게
        // (점검완료 잠금 중에는 적용하지 않음 — 보고서 생성 전이므로)
        const isLockedNoSelection = findingSelectionEnabled && !hasReport && !isCompletionLocked && !onToggleMeasureSelection &&
          (!finding.selectedMeasures || finding.selectedMeasures.length === 0);

        return (
          <div key={finding.id} className={isLockedNoSelection ? 'opacity-60' : ''}>
            <FindingDetail
              finding={finding}
              findingIndex={findingIdx}
              totalCount={findings.length}
              globalFindingNumber={globalNumber}
              editable={editable}
              editHandlers={editHandlers}
              tableMode={true}
              isSelected={isSelectedFinding && showHazardOverlay}
              hasBoundingBox={isBpPhoto ? false : !!hasBoundingBox}
              onClick={isBpPhoto ? undefined : (hasBoundingBox ? () => handleFindingSelect(finding.id, true) : undefined)}
              collapsible={true}
              defaultExpanded={true}
              hasHazardEdit={isBpPhoto ? false : hasHazardEdit}
              onHazardRevert={isBpPhoto ? undefined : (hasHazardEdit && onHazardRestore ? () => onHazardRestore(finding.id) : undefined)}
              onResolveHazard={isBpPhoto ? undefined : onResolveHazard}
              hasReport={hasReport}
              hazardResolutionEnabled={isBpPhoto ? false : hazardResolutionEnabled}
              findingSelectionEnabled={findingSelectionEnabled}
              selectedMeasures={finding.selectedMeasures}
              onToggleMeasureSelection={findingSelectionEnabled && onToggleMeasureSelection ? (measure, selected) => onToggleMeasureSelection(finding.id, measure, selected) : undefined}
              companyId={companyId}
              siteId={siteId}
              currentUserId={currentUserId}
              isMyCompleted={isMyCompleted}
              isPrimary={isPrimary}
              isOwner={isOwner}
              riskConfig={riskConfig}
              photoProcessType={photo.processType}
              equipmentProcessType={photo.equipment?.processType}
            />
          </div>
        );
      });
    }
    /*
     * 분석 실패 사유는 **일부러** 보이지 않는다(아래 「분석에 실패했습니다.」).
     *
     * 서버 `InspectionPhoto.analysisError`에 사유가 실리긴 하지만 그것은 날것
     * `error.message`다 — `photo-analysis.processor.ts:429·802`가 provider 예외 메시지를 그대로
     * 쓰고 `inspections.service.ts:1397`은 영문 내부 문자열(`'Inspection was deleted'`)을
     * 넣는다. 즉 **지금 값은 사용자에게 보일 수 없는 값**이다: 한글 UI에 영문·내부 사정이
     * 섞여 뜨고, provider 예외 본문에 무엇이 실릴지는 우리가 통제하지 않는다.
     *
     * **처방은 「이 값을 띄우는 것」이 아니다** — 서버가 **사용자용 «코드»**(안정된 enum,
     * 예: `PROVIDER_TIMEOUT`·`UNSUPPORTED_FORMAT`)를 주고 **문구는 클라가 소유**하는 것이다.
     * 그러면 번역·어휘·길이를 화면이 정하고, 서버 메시지가 바뀌어도 UI가 흔들리지 않는다.
     * 그 코드가 생기기 전까지 web 쪽 선언은 두지 않는다 — 2026-10-05에 세 층의 빈 선언
     * (`analysisSummary`·`analysisError`)을 걷었다. 읽는 곳 0·채우는 곳 0인데 「끝에서 끝까지
     * 배선된 것처럼」 보여 다음 사람을 속이는 자리였다.
     */
    return (
      <div className="px-2.5 py-3 text-center text-sm text-ink-500">
        {photo.analysisStatus === 'COMPLETED'
          ? (photo.purpose === 'BEST_PRACTICE'
            ? '발견된 우수사례 없음'
            : focusCategories.length > 0
              ? '발견된 중점 위험요인 없음'
              : '발견된 위험요인 없음')
          : photo.analysisStatus === 'ANALYZING'
            ? (focusCategories.length > 0
              ? '사진에서 중점 위험요인을 찾고 있습니다...'
              : '사진을 분석하고 있습니다...')
            : photo.analysisStatus === 'FAILED'
              ? '분석에 실패했습니다.'
              : '분석 대기 중입니다.'}
      </div>
    );
  };

  return (
    <div>
      {/* 통합 테이블 */}
      <div
        className={`border ${isAligned ? 'rounded-card-lg shadow-card-sm' : 'rounded-card'} ${photo.purpose === 'BEST_PRACTICE' ? 'border-emerald-300' : isAligned ? 'border-line' : 'border-ink-300'} ${isSelected ? 'ring-2 ring-brand-500' : ''} ${allFindingsExcluded ? 'opacity-60' : ''}`}
        style={{ fontSize: '13px' }}
      >
        {/* 헤더+사진 영역 (overflow-hidden으로 rounded corner 유지, 발견사항 드롭다운은 밖으로 넘칠 수 있음) */}
        <div className={`overflow-hidden ${isAligned ? 'rounded-t-card-lg' : 'rounded-t-card'}`}>
        {/* Desktop 헤더 (4열) */}
        <div className={`hidden sm:grid grid-cols-[6rem_1fr_6rem_1fr] border-b ${headerRowClass} ${dividerClass}`}>
          <div
            className={`px-2.5 py-1.5 font-medium text-ink-600 border-r ${dividerClass} text-xs flex items-center gap-1.5${selectable && !hasReport ? ' cursor-pointer hover:bg-ink-200 select-none' : ''}`}
            onClick={selectable && !hasReport ? () => onSelectionChange?.(!isSelected) : undefined}
          >
            {selectable && !hasReport && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelectionChange?.(e.target.checked);
                }}
                className={selectCheckboxClass}
              />
            )}
            사진
          </div>
          <div className="px-2.5 py-1.5 font-semibold text-ink-800 text-sm flex items-center gap-2">
            {photoNumber}
            {/* 분석 상태 */}
            {getAnalysisStatusBadge()}
            {/* 우수사례 뱃지 */}
            {photo.purpose === 'BEST_PRACTICE' && (
              <Badge tone="emerald" shape="rounded">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
                우수사례
              </Badge>
            )}
            {/* 등록자 */}
            {showAddedBy && photo.addedBy && (
              <span className="text-xs text-ink-500 font-normal">{getEvidenceDisplayName(photo.addedBySnapshotName ?? (photo.addedBy as any).signerName ?? null, { name: photo.addedBy.name })}</span>
            )}
          </div>
          <div className={`px-2.5 py-1.5 border-l border-r ${dividerClass} flex items-center ${isAligned ? 'text-[13.5px] font-bold tracking-[-0.005em] text-ink-700' : 'text-xs font-medium text-ink-600'}`}>{photo.purpose === 'BEST_PRACTICE' ? '우수사례' : '위험요인'}</div>
          <div className="px-2.5 py-1.5 text-sm flex items-center gap-2">
            {activeFindings.length > 0 ? (
              <Badge tone="amber" shape="rounded">
                {activeFindings.length}건
              </Badge>
            ) : (
              <span className="text-ink-400 text-xs">0건</span>
            )}
            {allFindingsExcluded && (
              <Badge tone="slate" shape="rounded">보고서 제외</Badge>
            )}
            {!allFindingsExcluded && includedFindingCount > 0 && includedFindingCount < activeFindings.length && (
              <Badge tone="brand" shape="rounded">{includedFindingCount}/{activeFindings.length}건 포함</Badge>
            )}
            {isReviewed && !findingSelectionEnabled && (
              <Badge tone="emerald" shape="rounded">검토완료</Badge>
            )}
            {addedByUserCount > 0 && (
              <Badge tone="brand" shape="rounded">
                사용자 추가 {addedByUserCount}건
              </Badge>
            )}
            {deletedCount > 0 && (
              <Badge tone="slate" shape="rounded">
                삭제 {deletedCount}건
              </Badge>
            )}
            {headerAction && <div className="ml-auto">{headerAction}</div>}
          </div>
        </div>

        {/* Mobile 헤더 (2열) */}
        <div className={`sm:hidden grid grid-cols-[4.5rem_1fr] border-b ${headerRowClass} ${dividerClass}`}>
          <div
            className={`px-2.5 py-1.5 font-medium text-ink-600 border-r ${dividerClass} text-xs flex items-center gap-1.5${selectable && !hasReport ? ' cursor-pointer hover:bg-ink-200 select-none' : ''}`}
            onClick={selectable && !hasReport ? () => onSelectionChange?.(!isSelected) : undefined}
          >
            {selectable && !hasReport && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelectionChange?.(e.target.checked);
                }}
                className={selectCheckboxClass}
              />
            )}
            사진
          </div>
          <div className="px-2.5 py-1.5 font-semibold text-ink-800 text-sm flex items-center gap-2">
            {photoNumber}
            {getAnalysisStatusBadge()}
            {photo.purpose === 'BEST_PRACTICE' && (
              <Badge tone="emerald" shape="rounded">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
                우수사례
              </Badge>
            )}
            {activeFindings.length > 0 && (
              <span className="text-xs text-ink-500 font-normal">{photo.purpose === 'BEST_PRACTICE' ? '우수사례' : '위험요인'} {activeFindings.length}건</span>
            )}
            {allFindingsExcluded && (
              <Badge tone="slate" shape="rounded">미포함</Badge>
            )}
            {!allFindingsExcluded && includedFindingCount > 0 && includedFindingCount < activeFindings.length && (
              <Badge tone="brand" shape="rounded">{includedFindingCount}/{activeFindings.length}</Badge>
            )}
            {isReviewed && !findingSelectionEnabled && (
              <Badge tone="emerald" shape="rounded">검토완료</Badge>
            )}
            {addedByUserCount > 0 && (
              <Badge tone="brand" shape="rounded">
                추가 {addedByUserCount}건
              </Badge>
            )}
            {deletedCount > 0 && (
              <Badge tone="slate" shape="rounded">
                삭제 {deletedCount}건
              </Badge>
            )}
            {headerAction && <div className="ml-auto">{headerAction}</div>}
          </div>
        </div>

        {/* 사진 행 (full-width) - horizontalLayout에서는 좌측 패널로 이동 */}
        {!horizontalLayout && (
        <div className={`relative border-b ${dividerClass}`} ref={photoContainerRef}>
          <PhotoMetaSection
                photo={photo}
                canEdit={editable && !hasReport && !isCompletionLocked}
                companyId={companyId}
                siteId={siteId}
                inspectionId={inspectionId}
                onPhotoMetaUpdate={onPhotoMetaUpdate}
              />
          {imageError ? (
            <div className="w-full aspect-[4/3] bg-ink-100 flex items-center justify-center text-ink-400">
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          ) : (
            <div className="relative group">
              <img
                src={normalizeFileUrl(photo.url)}
                alt={`사진 ${index}`}
                className="w-full aspect-[4/3] object-contain bg-ink-900"
                onError={() => setImageError(true)}
              />

              {/* 어노테이션 오버레이 */}
              {showAnnotationOverlay && companyId && inspectionId && imageDisplayInfo.width > 0 && (
                <AnnotationOverlay
                  companyId={companyId}
                  inspectionId={inspectionId}
                  photoId={photo.id}
                  containerWidth={imageDisplayInfo.width}
                  containerHeight={imageDisplayInfo.height}
                  offsetX={imageDisplayInfo.offsetX}
                  offsetY={imageDisplayInfo.offsetY}
                  visible={showAnnotationOverlay}
                />
              )}

              {/* 위험영역 오버레이 */}
              {showHazardOverlay && hazardAreas.length > 0 && (
                canEditHazard ? (
                  <InteractiveHazardEditor
                    hazards={hazardAreas}
                    containerWidth={imageDisplayInfo.width}
                    containerHeight={imageDisplayInfo.height}
                    offsetX={imageDisplayInfo.offsetX}
                    offsetY={imageDisplayInfo.offsetY}
                    selectedHazardId={effectiveSelectedFindingId}
                    onHazardSelect={handleHazardSelect}
                    onHazardChange={handleHazardChangeInternal}
                    onHazardChangeEnd={handleHazardChangeEnd}
                    onHazardDelete={handleHazardDelete}
                    riskConfig={riskConfig}
                  />
                ) : (
                  <HazardOverlay
                    hazards={hazardAreas}
                    containerWidth={imageDisplayInfo.width}
                    containerHeight={imageDisplayInfo.height}
                    offsetX={imageDisplayInfo.offsetX}
                    offsetY={imageDisplayInfo.offsetY}
                    selectedHazardId={effectiveSelectedFindingId}
                    onHazardClick={handleHazardSelect}
                    riskConfig={riskConfig}
                  />
                )
              )}

              {/* 분석 중 오버레이 */}
              {photo.analysisStatus === 'ANALYZING' && (
                <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center z-20">
                  <div className="relative w-16 h-16">
                    <div className="absolute inset-0 border-4 border-brand-200 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-transparent border-t-brand-500 rounded-full animate-spin"></div>
                  </div>
                  <p className="mt-4 text-white text-sm font-medium">
                    {photo.purpose === 'BEST_PRACTICE'
                      ? '우수사례를 분석하고 있습니다'
                      : '사진에서 위험요인을 분석하고 있습니다'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        )}
        </div>{/* /헤더+사진 래퍼 */}

        {/* 사진 + 발견사항: horizontalLayout일 때 좌우 배치 */}
        {horizontalLayout ? (
          <div className="flex items-start">
            {/* 좌측: 메타 + 사진 (self-stretch로 우측과 높이 맞춤 → sticky 동작) */}
            <div className={`w-1/2 border-r ${dividerClass} self-stretch`}>
              {/* 메타 (스크롤) */}
              <PhotoMetaSection
                photo={photo}
                canEdit={editable && !hasReport && !isCompletionLocked}
                companyId={companyId}
                siteId={siteId}
                inspectionId={inspectionId}
                onPhotoMetaUpdate={onPhotoMetaUpdate}
              />
              {/* 사진 (sticky) */}
              <div
                className="sticky"
                style={{ top: stickyTopOffset }}
                ref={photoContainerRef}
              >
              {imageError ? (
                <div
                  className="w-full aspect-[4/3] bg-ink-100 flex items-center justify-center text-ink-400"
                >
                  <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              ) : (
                <div className="relative group">
                  <img
                    src={normalizeFileUrl(photo.url)}
                    alt={`사진 ${index}`}
                    className="w-full object-contain bg-ink-900"
                    onError={() => setImageError(true)}
                  />
                  {showAnnotationOverlay && companyId && inspectionId && imageDisplayInfo.width > 0 && (
                    <AnnotationOverlay
                      companyId={companyId}
                      inspectionId={inspectionId}
                      photoId={photo.id}
                      containerWidth={imageDisplayInfo.width}
                      containerHeight={imageDisplayInfo.height}
                      offsetX={imageDisplayInfo.offsetX}
                      offsetY={imageDisplayInfo.offsetY}
                      visible={showAnnotationOverlay}
                    />
                  )}
                  {showHazardOverlay && hazardAreas.length > 0 && (
                    canEditHazard ? (
                      <InteractiveHazardEditor
                        hazards={hazardAreas}
                        containerWidth={imageDisplayInfo.width}
                        containerHeight={imageDisplayInfo.height}
                        offsetX={imageDisplayInfo.offsetX}
                        offsetY={imageDisplayInfo.offsetY}
                        selectedHazardId={effectiveSelectedFindingId}
                        onHazardSelect={handleHazardSelect}
                        onHazardChange={handleHazardChangeInternal}
                        onHazardChangeEnd={handleHazardChangeEnd}
                        onHazardDelete={handleHazardDelete}
                        riskConfig={riskConfig}
                      />
                    ) : (
                      <HazardOverlay
                        hazards={hazardAreas}
                        containerWidth={imageDisplayInfo.width}
                        containerHeight={imageDisplayInfo.height}
                        offsetX={imageDisplayInfo.offsetX}
                        offsetY={imageDisplayInfo.offsetY}
                        selectedHazardId={effectiveSelectedFindingId}
                        onHazardClick={handleHazardSelect}
                        riskConfig={riskConfig}
                      />
                    )
                  )}
                  {photo.analysisStatus === 'ANALYZING' && (
                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center z-20">
                      <div className="relative w-16 h-16">
                        <div className="absolute inset-0 border-4 border-brand-200 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-transparent border-t-brand-500 rounded-full animate-spin"></div>
                      </div>
                      <p className="mt-4 text-white text-sm font-medium">
                        {photo.purpose === 'BEST_PRACTICE'
                          ? '우수사례를 분석하고 있습니다'
                          : '사진에서 위험요인을 분석하고 있습니다'}
                      </p>
                    </div>
                  )}
                </div>
              )}
              </div>{/* /sticky 사진 */}
            </div>{/* /좌측 컬럼 */}

            {/* 우측: 발견사항 */}
            <div className="w-1/2 min-w-0">
              {renderFindings()}
              {editable && onAddFinding && (
                <button
                  type="button"
                  onClick={onAddFinding}
                  className={`w-full px-4 py-2.5 border-t ${dividerClass} border-dashed text-sm text-ink-500 transition-colors flex items-center justify-center gap-1 ${isBpPhoto ? 'hover:text-emerald-600 hover:bg-emerald-50' : 'hover:text-brand-600 hover:bg-brand-50'}`}
                >
                  <span className="text-lg leading-none">+</span> {isBpPhoto ? '우수사례 추가' : '발견사항 추가'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {renderFindings()}
            {/* 발견사항 추가 버튼 */}
            {editable && onAddFinding && (
              <button
                type="button"
                onClick={onAddFinding}
                className={`w-full px-4 py-2.5 border-t ${dividerClass} border-dashed text-sm text-ink-500 transition-colors flex items-center justify-center gap-1 ${isBpPhoto ? 'hover:text-emerald-600 hover:bg-emerald-50' : 'hover:text-brand-600 hover:bg-brand-50'}`}
              >
                <span className="text-lg leading-none">+</span> {isBpPhoto ? '우수사례 추가' : '발견사항 추가'}
              </button>
            )}
          </>
        )}
      </div>

      {/* 검토 완료 토글 - 테이블 외부 (findingSelectionEnabled 시 숨김) */}
      {onReviewToggle && !reviewDisabled && !findingSelectionEnabled && (
        <div className="mt-2 flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isReviewed}
              onChange={(e) => onReviewToggle(e.target.checked)}
              className="w-4 h-4 rounded border-ink-300 text-green-500 focus:ring-green-500"
            />
            <span className="text-sm text-ink-700">검토완료</span>
          </label>
        </div>
      )}
    </div>
  );
}

// FindingEditHandlers를 re-export
export type { FindingEditHandlers };

export default PhotoCardVertical;
